import { useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import styles from './kdt-bebj-17th.module.scss'

interface IProjectTabProps {
  projectDetailList: {
    category: string
    imgUrlPc: string
    imgUrlM: string
    title: string
    descList: string[]
    contents: {
      contentsId: string
      contentsIntro: string
      contentsInner: string[]
    }[]
  }[]
}

const ProjectTab = ({ projectDetailList }: IProjectTabProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [value, setValue] = useState(projectDetailList[0]?.category || '')
  const onClickTab = (step: string) => {
    setValue(step)
  }
  const selectedDetail = projectDetailList.find(detail => detail.category === value)
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({})
  const toggleOpen = (id: string) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  return (
    <>
      <ul className="flex flex-row gap-3">
        {projectDetailList.map(detail => (
          <li
            key={detail.category}
            className="flex flex-1 flex-row items-center gap-8"
            onClick={() => onClickTab(detail.category)}
          >
            <div
              className={`${
                value === detail.category
                  ? 'border-label-assistive bg-white'
                  : 'border-[#2E2E2E] bg-[#2E2E2E]'
              } flex-1 cursor-pointer rounded-t-lg border border-b-0 px-4 py-3 lg:px-8 lg:py-6`}
            >
              <h6
                className={`${
                  value === detail.category ? 'text-yellow-450' : 'text-label-alternative'
                } whitespace-pre-line text-center text-base font-bold leading-normal lg:whitespace-normal lg:text-left lg:text-2xl`}
              >
                {detail.category}
              </h6>
            </div>
          </li>
        ))}
      </ul>

      <div className="-mt-[1px] w-full rounded-b-lg border border-label-assistive bg-white p-4 lg:p-8">
        {selectedDetail && (
          <div className="w-full">
            <div className="mb-3 flex flex-col gap-3 border-b border-[#CFCFCF] pb-3 lg:mb-10 lg:flex-row lg:items-center lg:gap-10 lg:pb-10">
              <div className="w-full overflow-hidden rounded border border-label-assistive lg:w-auto lg:flex-1">
                <Image
                  src={isMobile ? selectedDetail.imgUrlM : selectedDetail.imgUrlPc}
                  layout="fill"
                  objectFit="cover"
                  className="!relative overflow-hidden rounded-lg"
                  alt="-"
                />
              </div>
              <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1 lg:gap-5">
                <h6 className="whitespace-pre-line text-sm font-bold text-label-normal lg:whitespace-normal lg:text-xl">
                  {selectedDetail.title}
                </h6>
                <ul className="flex flex-col gap-1">
                  {selectedDetail.descList.map(desc => (
                    <li
                      key={desc}
                      className={`${styles.text_dot} ${styles.text_dot_project1} text-sm leading-normal text-label-normal lg:text-base`}
                    >
                      {desc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col gap-2 overflow-hidden lg:flex-row lg:flex-wrap lg:items-start lg:gap-7">
              {selectedDetail.contents.map(content => {
                const isOpen = openAccordions[content.contentsId] || false
                return (
                  <div
                    key={content.contentsId}
                    className="w-full rounded bg-[#F2F2F2] lg:w-[calc(50%-14px)]"
                  >
                    <div
                      onClick={() => toggleOpen(content.contentsId)}
                      className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-4 lg:gap-2 lg:p-6 ${
                        isOpen ? 'pb-3 lg:pb-4' : 'pb-4 lg:pb-6'
                      } `}
                    >
                      <p className="flex-1 whitespace-pre-line text-sm font-bold lg:whitespace-normal lg:text-base lg:font-semibold">
                        {content.contentsIntro}
                      </p>
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-300 lg:h-6 lg:w-6 ${
                          isOpen ? 'rotate-180' : ''
                        } text-label-neutral`}
                      />
                    </div>
                    {isOpen && content.contentsInner && (
                      <ul className="flex flex-col gap-1 px-3 pb-4 lg:gap-0.5 lg:px-6 lg:pb-6">
                        {content.contentsInner.map(inner => (
                          <li key={inner}>
                            <p
                              className={`${styles.text_dot} ${styles.text_dot_project2} whitespace-pre-line text-sm leading-normal text-label-neutral lg:whitespace-normal`}
                            >
                              {inner}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ProjectTab
